/**
 * Xano Questions Service
 * Handles saving and managing questions in the database
 */

import { xanoClient } from './client';

import type { Question, QuestionType, AnswerOption } from '../../shared/types';
import type { ReviewTimeData } from '../../shared/types/timeTracking';

// Xano question structure based on API documentation
interface XanoQuestion {
  id?: string;
  created_at?: number;
  updated_at?: number;
  question_text: string;
  detailed_explanation: string;
  difficulty_level: number;
  generation_cost: number;
  token_usage?: {
    model_used?: string;
    total_tokens?: number;
    prompt_tokens?: number;
    completion_tokens?: number;
    generation_time_ms?: number;
  };
  requires_image: boolean;
  review_status?: 'pending' | 'in_review' | 'approved' | 'rejected' | 'needs_revision';
  quality_score?: number;
  is_published?: boolean;
  usage_count?: number;
  revision_notes?: string;
  question_type_id?: number;
  question_images_id?: string | null;
  medical_tags_id?: Array<{
    medical_tag: {
      id: number;
      tag_name: string;
      color_code: string;
      usage_count: number;
    };
  }> | number[];
  model_selections_id?: number;
  reviewer_id?: number;
  question_object?: any; // Store the complete question structure from AI
  exam_step_id?: number; // Link to exam step
  question_pool_id?: number; // Link to question pool
  _medical_specialties?: any;
  _exam_steps?: {
    id: number;
    name: string;
    _exam_types?: {
      id: number;
      name: string;
    };
  };
  _question_pools?: {
    id: number;
    name: string;
  };
  _question_pool?: {
    id: number;
    name: string;
  };
  topic_id?: number;
}

// Question type mapping - these should match your database
const QUESTION_TYPE_MAP: Record<QuestionType, number> = {
  'single_choice': 1,
  'multiple_select': 2,
  'true_false': 3,
  'fill_blanks': 3,
  'image_based': 8,
  'clinical_scenario': 9,
  'lab_values': 7,
  'management_steps': 10
};

// Response structure for paginated queries
interface QuestionsResponse {
  itemsReceived: number;
  curPage: number;
  nextPage: number;
  prevPage: number;
  offset: number;
  perPage: number;
  itemsTotal: number;
  pageTotal: number;
  items: Array<XanoQuestion & {
    question_object?: any; // The complete question structure from AI
  }>;
}

class QuestionsService {
  /**
   * Query questions with optional status filter and pagination
   */
  async queryQuestions(status?: string, page: number = 1, perPage: number = 30): Promise<QuestionsResponse> {
    try {
      // Build params object for xanoClient
      const params: any = {
        page: page,
        per_page: perPage
      };
      
      if (status) {
        // Add review_status parameter for API filtering
        params.review_status = status;
      }
      
      // Use xanoClient.get which handles parameter formatting
      const response = await xanoClient.get<QuestionsResponse>('/questions', params);
      
      return response;
    } catch (error) {
      console.error('Failed to query questions:', error);
      throw new Error('Failed to load questions');
    }
  }
  
  /**
   * Get questions for review queue
   */
  async getReviewQueueQuestions(status?: 'pending' | 'in_review' | 'approved'): Promise<XanoQuestion[]> {
    try {
      // Xano uses different status values than our internal ones
      let reviewStatus: string | undefined;
      if (status) {
        reviewStatus = status;
      }
      
      const response = await this.queryQuestions(reviewStatus);
      return response.items;
    } catch (error) {
      // Failed to get review queue questions
      throw error;
    }
  }
  /**
   * Save a generated question to the database
   */
  async saveQuestion(
    question: Question,
    promptId: string,
    generationCost: number,
    tokenUsage: { input_tokens: number; output_tokens: number; },
    userId?: number,
    tagIds?: number[],
    examStepId?: number,
    questionPoolId?: number
  ): Promise<string> {
    try {
      // Get model selection ID from the AI model
      const modelSelectionId = this.getModelSelectionId(question.aiModel);

      // Prepare the complete question object with answer options
      const questionObject = {
        questionText: question.questionText,
        questionType: question.questionType,
        answerOptions: question.answerOptions,
        detailedExplanation: question.detailedExplanation,
        difficultyLevel: question.difficultyLevel,
        requiresImage: question.requiresImage
      };

      // Prepare the question data according to the final Xano API format
      const xanoQuestion: XanoQuestion = {
        updated_at: Date.now(),
        question_text: question.questionText,
        detailed_explanation: question.detailedExplanation || '',
        difficulty_level: question.difficultyLevel,
        generation_cost: generationCost,
        token_usage: {
          prompt_tokens: tokenUsage.input_tokens,
          completion_tokens: tokenUsage.output_tokens,
          total_tokens: tokenUsage.input_tokens + tokenUsage.output_tokens,
          model_used: question.aiModel,
          generation_time_ms: Date.now()
        },
        requires_image: question.requiresImage || false,
        review_status: 'pending',
        quality_score: 0,
        is_published: false,
        usage_count: 0,
        revision_notes: '',
        question_type_id: QUESTION_TYPE_MAP[question.questionType],
        medical_tags_id: tagIds || [],
        model_selections_id: modelSelectionId,
        question_object: questionObject, // Store the complete question structure
        exam_step_id: examStepId,
        question_pool_id: questionPoolId
        // reviewer_id will be set during review
      };

      // Save to database using the correct API endpoint
      const response = await xanoClient.post<XanoQuestion>('/questions', xanoQuestion);
      
      if (!response.id) {
        throw new Error('Question saved but no ID returned');
      }

      return response.id;
    } catch (error) {
      // Failed to save question
      throw new Error(error instanceof Error ? error.message : 'Failed to save question to database');
    }
  }

  /**
   * Format answer options according to Xano API structure
   */
  private formatAnswerOptions(
    options: AnswerOption[],
    questionType: QuestionType
  ): any {
    if (questionType === 'true_false') {
      const correctOption = options.find(opt => opt.isCorrect);
      return {
        type: 'true_false',
        correct_answer: correctOption?.text === 'True',
        options: [
          {
            id: 'true',
            text: 'True',
            is_correct: correctOption?.text === 'True'
          },
          {
            id: 'false',
            text: 'False',
            is_correct: correctOption?.text === 'False'
          }
        ]
      };
    }

    // For single_choice and multiple_select
    const correctAnswers = options.filter(opt => opt.isCorrect);
    
    // Keep the original IDs from the generated question (opt_1, opt_2, etc.)
    const mappedOptions = options.map(opt => ({
      id: opt.id,
      text: opt.text,
      is_correct: opt.isCorrect
    }));
    
    return {
      type: questionType === 'single_choice' ? 'multiple_choice' : 'multiple_select',
      options: mappedOptions,
      correct_answer: questionType === 'single_choice' 
        ? correctAnswers[0]?.id || 'opt_1'
        : correctAnswers.map(opt => opt.id),
      allow_multiple: questionType === 'multiple_select'
    };
  }


  /**
   * Get model selection ID from AI model type
   */
  private getModelSelectionId(aiModel: string): number {
    // Map AI models to model_selection_id based on your database
    const modelMap: Record<string, number> = {
      'claude-opus-4': 1,
      'claude-sonnet-4': 2,
      'claude-haiku': 3,
      'gpt-4': 4,
      'gpt-3.5': 5,
      'gemini-pro': 6,
      'cohere-command': 7
    };
    
    return modelMap[aiModel] || 2; // Default to Claude Sonnet
  }

  /**
   * Get a question by ID
   */
  async getQuestionById(questionId: string): Promise<Question | null> {
    try {
      const response = await xanoClient.get<XanoQuestion>(`/questions/${questionId}`);
      return this.transformToAppFormat(response);
    } catch (error) {
      // Failed to fetch question
      return null;
    }
  }

  /**
   * Update question review status
   */
  async updateQuestionStatus(
    questionId: string,
    status: 'approved' | 'rejected' | 'needs_revision',
    notes?: string,
    timeData?: ReviewTimeData | null,
    reviewerId?: number
  ): Promise<void> {
    try {
      const updateData: any = {
        review_status: status,
        revision_notes: notes,
        updated_at: Date.now()
      };
      
      // Include reviewer_id if provided
      if (reviewerId) {
        updateData.reviewer_id = reviewerId;
      }
      
      // Include time tracking data if provided
      if (timeData) {
        updateData.review_time_data = {
          total_seconds: timeData.totalSeconds,
          active_seconds: timeData.activeSeconds,
          idle_seconds: timeData.idleSeconds,
          started_at: timeData.startedAt,
          completed_at: timeData.completedAt,
          timed_out: timeData.timedOut
        };
      }
      
      await xanoClient.patch(`/questions/${questionId}`, updateData);
    } catch (error) {
      // Failed to update question status
      throw new Error('Failed to update question status');
    }
  }

  /**
   * Transform Xano question to app format
   */
  private transformToAppFormat(xanoQuestion: XanoQuestion): Question {
    // Extract answer options from question_object
    let answerOptions: AnswerOption[] = [];
    let questionType: QuestionType = 'single_choice';

    if (xanoQuestion.question_object && typeof xanoQuestion.question_object === 'object') {
      answerOptions = xanoQuestion.question_object.answerOptions || [];
      questionType = xanoQuestion.question_object.questionType || 'single_choice';
    }

    return {
      id: xanoQuestion.id!,
      questionText: xanoQuestion.question_text,
      questionType,
      difficultyLevel: xanoQuestion.difficulty_level as any,
      answerOptions,
      detailedExplanation: xanoQuestion.detailed_explanation,
      aiModel: 'claude-sonnet-4', // Based on model_selection_id
      requiresImage: xanoQuestion.requires_image,
      generationCost: xanoQuestion.generation_cost,
      createdAt: new Date(xanoQuestion.created_at || Date.now()),
      createdBy: '', // TODO: Look up user
      status: xanoQuestion.review_status === 'approved' ? 'approved' : 
              xanoQuestion.review_status === 'rejected' ? 'rejected' : 
              xanoQuestion.review_status === 'needs_revision' ? 'under_review' : 'draft'
    };
  }
}

export const questionsService = new QuestionsService();

// Export for use in components
export const saveGeneratedQuestion = (
  question: Question,
  promptId: string,
  cost: number,
  tokenUsage: any,
  userId?: number,
  tagIds?: number[],
  examStepId?: number,
  questionPoolId?: number
) => questionsService.saveQuestion(question, promptId, cost, tokenUsage, userId, tagIds, examStepId, questionPoolId);